// ZodiacPersonalityTab.tsx
import React, { useState } from "react";
import { UserCircle } from "lucide-react";
import AstriaMexicoShell from "../AstriaMexicoShell";
import { useMexicoColors } from "../AstriaMexicoThemeContext";
import {
  AstriaMexicoResultCard,
  AstriaMexicoFieldBlock,
  MXLoadingState,
  MXErrorState,
  MXChatCta,
} from "../AstriaMexicoCard";
import { MXTextInput, MXSubmitButton } from "../AstriaMexicoWizard";
import { useAstriaMexicoTab } from "../useAstriaMexicoTab";
import { AstriaMexicoZodiacPersonalityData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Zodiac Personality";

// Colors must be read INSIDE AstriaMexicoShell's subtree — these small
// components are rendered as `children`, not evaluated inline, so their hook
// calls resolve after the shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaMexicoZodiacPersonalityData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useMexicoColors();

  if (data) {
    return (
      <>
        <AstriaMexicoResultCard
          icon={<UserCircle className="w-5 h-5" />}
          title="Personalidad"
          subtitle="Basada en tu signo solar"
          shareText={`${data.core_trait}\n\n${data.closing}`}
        >
          <AstriaMexicoFieldBlock label="Rasgo Central" value={data.core_trait} />
          <AstriaMexicoFieldBlock label="En el Día a Día" value={data.everyday_style} />
          <AstriaMexicoFieldBlock label="Dónde Te Sirve Más" value={data.where_it_serves_you} />
          <AstriaMexicoFieldBlock label="Dónde Te Puede Jugar en Contra" value={data.where_it_trips_you_up} />
          <AstriaMexicoFieldBlock label="Cierre" value={data.closing} />
        </AstriaMexicoResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Empezar Una Nueva Lectura
        </button>
        <MXChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaMexicoResultCard icon={<UserCircle className="w-5 h-5" />} title="Personalidad">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaMexicoResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { colors } = useMexicoColors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Personalidad
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          Una lectura de personalidad basada en tu signo solar — cálida y grounded, sin sonar a
          horóscopo genérico.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <MXTextInput value={dob} onChange={setDob} placeholder="Fecha de nacimiento (DD/MM/AAAA)" />
      </div>

      <MXSubmitButton onClick={onSubmit} disabled={!dob.trim()} label="Revelar Mi Personalidad" />
    </>
  );
};

const ZodiacPersonalityTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaMexicoTab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Dame mi lectura de Personalidad. Mi fecha de nacimiento: ${dob.trim()}.`);

  const data = message?.astriaMexicoData as AstriaMexicoZodiacPersonalityData | undefined;

  if (started) {
    return (
      <AstriaMexicoShell active="zodiac-personality">
        {loading && <MXLoadingState label="Leyendo tu signo…" />}
        {!loading && error && <MXErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaMexicoShell>
    );
  }

  return (
    <AstriaMexicoShell active="zodiac-personality">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaMexicoShell>
  );
};

export default ZodiacPersonalityTab;
